import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation, useBlocker, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import api from '../../redux/features/auth/axiosAuthHelper';
import { type RootState } from '../../redux/store';
import checkRoutePermissions from '../../routes/appRoutePermissions';
import colorConfigs from '../../configs/colorConfigs';
import {
  validDefaults,
  workflowLabels,
  type WorkflowDefaults,
} from './accountApi';
import {
  IMPORT_ENDPOINT,
  downloadImportFile,
  importError,
  validImport,
  validImportRow,
  type ImportSummary,
  type ImportRow,
} from './importApi';

const display = (value: unknown) =>
  value === undefined || value === null || value === ''
    ? '—'
    : typeof value === 'boolean'
      ? value
        ? 'Yes'
        : 'No'
      : String(value);
const outcomeLabels = {
  ready: 'Ready',
  created: 'Created',
  skipped: 'Skipped duplicate',
  rejected: 'Needs correction',
};

/** Review saved row outcomes, confirm creation and guard unfinished imports. */
export default function CustomerAccountImportsPage() {
  const auth = useSelector((state: RootState) => state.auth);
  const allowed =
    checkRoutePermissions(auth, { state: 'customer-accounts' }) === true;
  const { importId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const newImport = location.pathname.endsWith('/new');
  const [defaults, setDefaults] = useState<WorkflowDefaults | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [history, setHistory] = useState<ImportSummary[]>([]);
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [count, setCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState('all');
  const [reload, setReload] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const allowNavigation = useRef(false);
  const uploadToken = useRef('');
  const input = useRef<HTMLInputElement>(null);
  const operation = useRef<AbortController | null>(null);
  const lastConfirmed = useRef('');
  useEffect(() => {
    setOffset(0);
    setFilter('all');
    setSummary(null);
    setFile(null);
    setError('');
    setConfirmOpen(false);
    allowNavigation.current = false;
    uploadToken.current = '';
    setBusy('');
    lastConfirmed.current = '';
    return () => operation.current?.abort();
  }, [importId, newImport]);
  useEffect(() => {
    if (!allowed) return;
    const controller = new AbortController();
    const config = {
      headers: { Authorization: `Bearer ${auth.access}` },
      signal: controller.signal,
      timeout: 30000,
    };
    setLoading(true);
    if (lastConfirmed.current !== importId) setError('');
    setRows([]);
    setHistory([]);
    const load = async () => {
      if (newImport) {
        const response = await api.get('customer-accounts/defaults/', config);
        if (!validDefaults(response.data)) throw new Error('Invalid defaults');
        if (!controller.signal.aborted) setDefaults(response.data);
      } else if (importId) {
        const [batch, page] = await Promise.all([
          api.get(`${IMPORT_ENDPOINT}${importId}/`, config),
          api.get(
            `${IMPORT_ENDPOINT}${importId}/rows/?limit=50&offset=${offset}&status=${filter}`,
            config,
          ),
        ]);
        if (
          !validImport(batch.data) ||
          !Number.isSafeInteger(page.data?.count) ||
          page.data.count < 0 ||
          page.data.count > 10000 ||
          !Array.isArray(page.data.results) ||
          page.data.results.length > 50 ||
          !page.data.results.every(validImportRow)
        )
          throw new Error('Invalid import response');
        if (!controller.signal.aborted) {
          setSummary(batch.data);
          if (
            (batch.data.status === 'completed' ||
              (batch.data.status === 'failed' && batch.data.error)) &&
            lastConfirmed.current === importId
          )
            setError('');
          setRows(page.data.results);
          setCount(page.data.count);
        }
      } else {
        const page = await api.get(
          `${IMPORT_ENDPOINT}?limit=20&offset=${offset}`,
          config,
        );
        if (
          !Number.isSafeInteger(page.data?.count) ||
          page.data.count < 0 ||
          !Array.isArray(page.data.results) ||
          page.data.results.length > 20 ||
          !page.data.results.every(validImport)
        )
          throw new Error('Invalid history');
        if (!controller.signal.aborted) {
          setHistory(page.data.results);
          setCount(page.data.count);
        }
      }
    };
    load()
      .catch((failure) => {
        if (!controller.signal.aborted) setError(importError(failure));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [allowed, auth.access, importId, newImport, offset, filter, reload]);

  const pendingImport = !!file || !!(summary && summary.ready > 0 && summary.status !== 'completed');
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    allowed && pendingImport && !allowNavigation.current && currentLocation.pathname !== nextLocation.pathname,
  );
  /** Page buttons and sidebar/history transitions use the same router-level guard. */
  const leave = (path: string) => {
    if (busy || path === location.pathname) return;
    navigate(path);
  };
  useEffect(() => {
    if (blocker.state === 'blocked' && !pendingImport && !busy) blocker.reset();
  }, [blocker, pendingImport, busy]);
  useEffect(() => {
    if (!pendingImport) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [pendingImport]);

  const choose = (files: FileList | null) => {
    if (busy || !files) return;
    setError('');
    setFile(null);
    uploadToken.current = '';
    if (files.length !== 1) {
      setError('Choose one CSV or XLSX file.');
      return;
    }
    const selected = files[0];
    if (!/\.(csv|xlsx)$/i.test(selected.name)) {
      setError('Choose a CSV or XLSX file.');
      return;
    }
    if (selected.size > 10 * 1024 * 1024) {
      setError('Files are limited to 10 MB. Split the file and try again.');
      return;
    }
    if (!selected.size) {
      setError('The file is empty.');
      return;
    }
    setFile(selected);
    uploadToken.current = uuid();
  };
  const upload = async () => {
    if (!file || !defaults || busy) return;
    const controller = new AbortController();
    operation.current = controller;
    const form = new FormData();
    form.append('file', file);
    form.append('upload_token', uploadToken.current);
    setBusy('Uploading file…');
    setError('');
    try {
      const response = await api.post(IMPORT_ENDPOINT, form, {
        headers: { Authorization: `Bearer ${auth.access}` },
        signal: controller.signal,
        timeout: 45000,
        onUploadProgress: (event) => {
          if (
            event.total &&
            event.loaded >= event.total &&
            !controller.signal.aborted
          )
            setBusy('Checking file…');
        },
      });
      if (controller.signal.aborted) return;
      if (!validImport(response.data)) throw new Error('Invalid preview');
      // Upload succeeded: switching to its saved preview is an intentional continuation.
      allowNavigation.current = true;
      navigate(`/customer-accounts/imports/${response.data.id}`);
    } catch (failure) {
      if (!controller.signal.aborted) setError(importError(failure));
    } finally {
      if (!controller.signal.aborted) setBusy('');
    }
  };
  const confirm = async () => {
    if (!summary || summary.ready === 0 || busy || loading) return;
    setConfirmOpen(false);
    const controller = new AbortController();
    operation.current = controller;
    lastConfirmed.current = summary.id;
    setBusy(`Importing ${summary.ready.toLocaleString()} accounts…`);
    setError('');
    try {
      const response = await api.post(
        `${IMPORT_ENDPOINT}${summary.id}/confirm/`,
        {},
        {
          headers: { Authorization: `Bearer ${auth.access}` },
          signal: controller.signal,
          timeout: 45000,
        },
      );
      if (controller.signal.aborted) return;
      if (!validImport(response.data)) throw new Error('Invalid result');
      setSummary(response.data);
      setReload((value) => value + 1);
    } catch (failure) {
      if (!controller.signal.aborted) {
        setError(importError(failure));
        setReload((value) => value + 1);
      }
    } finally {
      if (!controller.signal.aborted) setBusy('');
    }
  };
  const download = async (path: string, format: 'csv' | 'xlsx') => {
    if (busy) return;
    const controller = new AbortController();
    operation.current = controller;
    setBusy('Preparing download…');
    setError('');
    try {
      await downloadImportFile(path, format, auth.access, controller.signal);
    } catch (failure) {
      if (!controller.signal.aborted)
        setError(importError(failure, 'download'));
    } finally {
      if (!controller.signal.aborted) setBusy('');
    }
  };
  if (!allowed)
    return (
      <Alert severity="error">
        You do not have permission to import customer accounts.
      </Alert>
    );
  const pageSize = importId ? 50 : 20;
  return (
    <Box sx={{ p: { xs: 1.5, md: 2 }, maxWidth: 1500, mx: 'auto', height: '100dvh', minHeight: 360, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Typography
        component="h1"
        variant="h4"
        align="center"
        sx={{ color: colorConfigs.tables.headBg, fontWeight: 700, mb: 2 }}
      >
        Customer Account Imports
      </Typography>
      <Stack
        direction="row"
        spacing={1}
        sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
      >
        {!importId && <Button disabled={!!busy} onClick={() => leave('/customer-accounts')}>
          Back to accounts
        </Button>}
        <Button
          disabled={!!busy}
          onClick={() => leave('/customer-accounts/imports/new')}
          variant={newImport ? 'contained' : 'outlined'}
        >
          New import
        </Button>
        <Button
          disabled={!!busy}
          onClick={() => leave('/customer-accounts/imports')}
          variant={!newImport && !importId ? 'contained' : 'outlined'}
        >
          Import history
        </Button>
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pr: 0.5, display: importId ? 'flex' : 'block', flexDirection: 'column' }}>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              disabled={!!busy}
              onClick={() => setReload((value) => value + 1)}
            >
              Refresh
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      {busy && (
        <Box role="status" sx={{ mb: 2 }}>
          <Typography sx={{ mb: 1 }}>{busy}</Typography>
          <LinearProgress />
        </Box>
      )}
      {loading && <CircularProgress aria-label="Loading import" size={24} />}
      {newImport && (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h6">
            Upload and review before creating accounts
          </Typography>
          <Typography sx={{ mt: 1 }}>
            Use a template below. You will review duplicates and errors before
            confirming. Up to 10,000 data rows and 10 MB per file.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ my: 2 }}>
            <Button
              disabled={!!busy}
              variant="outlined"
              onClick={() => download('template/?file_format=csv', 'csv')}
            >
              Download CSV template
            </Button>
            <Button
              disabled={!!busy}
              variant="outlined"
              onClick={() => download('template/?file_format=xlsx', 'xlsx')}
            >
              Download XLSX template
            </Button>
          </Stack>
          <TableContainer
            sx={{
              mb: 2,
              height: 'auto',
              minHeight: 0,
              maxHeight: 'none',
              paddingBottom: 0,
            }}
          >
            <Table size="small" aria-label="Import columns">
              <TableHead>
                <TableRow>
                  <TableCell>Column</TableCell>
                  <TableCell>Rule</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>account_number</TableCell>
                  <TableCell>
                    Required text. Preserve casing and leading zeros; format
                    Excel cells as Text.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>account_name</TableCell>
                  <TableCell>
                    Optional text. Blank leaves the account unnamed.
                  </TableCell>
                </TableRow>
                {defaults &&
                  Object.entries(workflowLabels).map(([key, label]) => (
                    <TableRow key={key}>
                      <TableCell>{key}</TableCell>
                      <TableCell>
                        {label}: blank uses{' '}
                        {defaults[key as keyof WorkflowDefaults] ? 'Yes' : 'No'}
                        .
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Booleans: true/false, yes/no or 1/0. Blank uses the displayed
            default; other values are rejected. One header row; no extra or
            repeated column names. Customer Accounts export headers and spaced
            equivalents such as Account Number are accepted. Optional ID and
            Status columns are ignored; accounts are matched by Account Number.
            If CSV rows are flagged as having a spreadsheet safety prefix, use
            verified original text in an XLSX template, or an original XLSX
            export when available, to preserve the exact account text.
            Correction files leave Account Number and ambiguous text blank for
            you to verify.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Existing and archived accounts are skipped without changes.
            Identical repeats are skipped; repeated account numbers with
            conflicting settings need correction. XLSX: use one sheet named
            Customer Accounts, or Records from an export. Use values, not formulas,
            in the account fields.
          </Typography>
          <Box
            role="region"
            aria-label="File drop zone"
            onDragOver={(event) => {
              event.preventDefault();
              if (!busy) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              choose(event.dataTransfer.files);
            }}
            sx={{
              border: '2px dashed',
              borderColor: dragging ? colorConfigs.tables.headBg : '#b2c9c5',
              backgroundColor: dragging ? '#edf8f5' : '#fafcfb',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
            }}
          >
            <UploadFileOutlinedIcon
              sx={{ fontSize: 40, color: colorConfigs.tables.headBg }}
            />
            <Typography sx={{ mb: 1 }}>
              Drag and drop one CSV or XLSX file here
            </Typography>
            <input
              ref={input}
              type="file"
              accept=".csv,.xlsx"
              aria-label="Import file"
              style={{ display: 'none' }}
              onChange={(event) => {
                choose(event.target.files);
                event.target.value = '';
              }}
            />
            <Button
              variant="outlined"
              disabled={!!busy || !defaults || loading}
              onClick={() => input.current?.click()}
            >
              Choose file
            </Button>
            {file && (
              <Typography sx={{ mt: 1 }}>
                {file.name} · {(file.size / 1024).toFixed(1)} KB
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            disabled={!file || !defaults || !!busy || loading}
            onClick={upload}
          >
            Check file
          </Button>
        </Paper>
      )}
      {importId && summary && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, flexShrink: 0 }}>
            <Typography variant="h6" sx={{ overflowWrap: 'anywhere' }}>{summary.filename}</Typography>
            <Typography variant="body2" sx={{ mb: 1, overflowWrap: 'anywhere' }}>
              {summary.actor_email} ·{' '}
              {new Date(summary.created_at).toLocaleString()} · Import{' '}
              {summary.id}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{ flexWrap: 'wrap', gap: 1 }}
            >
              <Chip label={`${summary.total.toLocaleString()} rows checked`} />
              <Chip
                color="info"
                label={`${summary.ready.toLocaleString()} ready`}
              />
              <Chip
                color="success"
                label={`${summary.created.toLocaleString()} created`}
              />
              <Chip
                color="warning"
                label={`${summary.skipped.toLocaleString()} duplicates skipped`}
              />
              <Chip
                color="error"
                label={`${summary.rejected.toLocaleString()} need correction`}
              />
            </Stack>
            {summary.defaulted > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {summary.defaulted.toLocaleString()}{' '}
                {summary.defaulted === 1 ? 'row uses' : 'rows use'} defaults for
                blank settings. Review the effective values below.
              </Typography>
            )}
            {summary.status === 'completed' && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Import completed. Results and audit entries have been saved.
              </Alert>
            )}
            {summary.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {summary.error}
              </Alert>
            )}
            {summary.error && summary.total === 0 && (
              <Stack direction="row" sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                <Button variant="outlined" disabled={!!busy} onClick={() => download('template/?file_format=csv', 'csv')}>Download CSV template</Button>
                <Button variant="outlined" disabled={!!busy} onClick={() => download('template/?file_format=xlsx', 'xlsx')}>Download XLSX template</Button>
              </Stack>
            )}
            {summary.status !== 'completed' && summary.ready > 0 && (
              <>
                <Typography sx={{ mt: 1 }}>
                  Only valid new accounts will be created. Existing accounts
                  will not be changed.
                </Typography>
              </>
            )}
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}
            >
              <Button
                disabled={!!busy}
                onClick={() => {
                  lastConfirmed.current = '';
                  setReload((value) => value + 1);
                }}
              >
                Refresh saved result
              </Button>
              <Button
                disabled={!!busy || !summary.total}
                onClick={() => download(`${summary.id}/report/`, 'csv')}
              >
                Download report (CSV)
              </Button>
              <Button
                disabled={!!busy || !summary.rejected}
                onClick={() =>
                  download(`${summary.id}/report/?mode=corrections`, 'xlsx')
                }
              >
                Download rows to correct (XLSX)
              </Button>
            </Stack>
          </Paper>
          <TextField
            select
            size="small"
            label="Show rows"
            value={filter}
            onChange={(event) => {
              setOffset(0);
              setFilter(event.target.value);
            }}
            sx={{ minWidth: 240, mb: 2, flexShrink: 0, alignSelf: 'flex-start' }}
          >
            <MenuItem value="all">All rows</MenuItem>
            {Object.entries(outcomeLabels).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              height: 'auto',
              flex: 1,
              minHeight: 120,
              maxHeight: 'none',
              overflow: 'auto',
              paddingBottom: 0,
              scrollbarWidth: 'auto',
              '&::-webkit-scrollbar': { display: 'block' },
            }}
          >
            <Table stickyHeader size="small" aria-label="Import row results" sx={{ tableLayout: 'fixed', minWidth: 800, '& td': { overflowWrap: 'anywhere', whiteSpace: 'normal', verticalAlign: 'top' }, '& th': { top: 0, backgroundColor: colorConfigs.tables.headBg, color: '#fff', overflowWrap: 'anywhere', whiteSpace: 'normal' } }}>
              <colgroup>
                {[7, 18, 18, 17, 13, 27].map((width, index) => <col key={index} style={{ width: `${width}%` }} />)}
              </colgroup>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: colorConfigs.tables.headBg,
                    '& th': { color: '#fff' },
                  }}
                >
                  {[
                    'File row',
                    'Account Number',
                    'Account Name',
                    'Workflow / Send to lab / Override',
                    'Outcome',
                    'Explanation',
                  ].map((label) => (
                    <TableCell key={label}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.row_number}>
                    <TableCell>{row.row_number}</TableCell>
                    <TableCell>
                      {row.account_id ? (
                        <Link to={`/customer-accounts/${row.account_id}`}>
                          {display(row.source.account_number)}
                        </Link>
                      ) : (
                        display(row.source.account_number)
                      )}
                    </TableCell>
                    <TableCell>{display(row.source.account_name)}</TableCell>
                    <TableCell>
                      {Object.keys(workflowLabels)
                        .map((key) =>
                          display(row.values[key] ?? row.source[key]),
                        )
                        .join(' / ')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        sx={{ height: 'auto', minHeight: 24, '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }}
                        label={outcomeLabels[row.status]}
                        color={
                          row.status === 'created'
                            ? 'success'
                            : row.status === 'rejected'
                              ? 'error'
                              : row.status === 'skipped'
                                ? 'warning'
                                : 'info'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {row.reason || 'Ready to create.'}
                      {row.defaulted_fields.length > 0 && (
                        <Typography variant="caption" display="block">
                          Defaults:{' '}
                          {row.defaulted_fields
                            .map(
                              (key) =>
                                workflowLabels[key as keyof WorkflowDefaults],
                            )
                            .join(', ')}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      {!newImport && !importId && (
        <Paper variant="outlined">
          <TableContainer
            sx={{
              height: 'auto',
              minHeight: 0,
              maxHeight: '65vh',
              paddingBottom: 0,
              scrollbarWidth: 'auto',
              '&::-webkit-scrollbar': { display: 'block' },
            }}
          >
            <Table stickyHeader aria-label="Import history">
              <TableHead>
                <TableRow>
                  {[
                    'File',
                    'Imported by',
                    'Uploaded',
                    'Status',
                    'Created / Skipped / Rejected',
                    '',
                  ].map((label, index) => (
                    <TableCell key={index}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.filename}</TableCell>
                    <TableCell>{item.actor_email}</TableCell>
                    <TableCell>
                      {new Date(item.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {item.status === 'ready'
                        ? 'Awaiting confirmation'
                        : item.status === 'completed'
                          ? 'Completed'
                          : 'Needs attention'}
                    </TableCell>
                    <TableCell>
                      {item.created} / {item.skipped} / {item.rejected}
                    </TableCell>
                    <TableCell>
                      <Button
                        component={Link}
                        to={`/customer-accounts/imports/${item.id}`}
                      >
                        View results
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {!loading && !error && !history.length && (
            <Typography sx={{ p: 2 }}>
              No imports yet. Start a new import using a template.
            </Typography>
          )}
        </Paper>
      )}
      {!newImport && (
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ mt: 1, flexShrink: 0 }}
        >
          <Typography variant="body2">
            {count
              ? `${offset + 1}–${Math.min(offset + pageSize, count)} of ${count.toLocaleString()} ${importId ? 'rows' : 'imports'}`
              : '0 results'}
          </Typography>
          <Button
            disabled={loading || !!busy || offset === 0}
            onClick={() => setOffset((value) => Math.max(0, value - pageSize))}
          >
            Previous
          </Button>
          <Button
            disabled={loading || !!busy || offset + pageSize >= count}
            onClick={() => setOffset((value) => value + pageSize)}
          >
            Next
          </Button>
        </Stack>
      )}
      </Box>
      {importId && (
        <Stack component="footer" direction="row" alignItems="center" justifyContent="space-between" sx={{ flexShrink: 0, borderTop: '1px solid #d5dedc', backgroundColor: '#fff', mt: 1, px: 2, py: 1.5, gap: 2, flexWrap: 'wrap' }}>
          <Button variant="outlined" disabled={!!busy} onClick={() => leave('/customer-accounts')}>
            {summary?.status === 'completed' ? 'View accounts' : 'Back to accounts'}
          </Button>
          {summary && summary.status !== 'completed' && summary.ready > 0 && (
            <Button variant="contained" disabled={!!busy || loading || !!error} onClick={() => setConfirmOpen(true)}>
              {summary.status === 'failed' ? 'Retry import of' : 'Import'} {summary.ready.toLocaleString()} {summary.ready === 1 ? 'account' : 'accounts'}
            </Button>
          )}
        </Stack>
      )}
      <Dialog open={confirmOpen} onClose={() => { if (!busy) setConfirmOpen(false); }} aria-labelledby="confirm-import-title">
        <DialogTitle id="confirm-import-title">Create {summary?.ready.toLocaleString()} accounts?</DialogTitle>
        <DialogContent>
          <DialogContentText>Only the {summary?.ready.toLocaleString()} ready {summary?.ready === 1 ? 'account will' : 'accounts will'} be created.</DialogContentText>
          {!!summary?.skipped && <Alert severity="warning" sx={{ mt: 2 }}>{summary.skipped.toLocaleString()} duplicate {summary.skipped === 1 ? 'row will' : 'rows will'} be skipped. Existing accounts will not be changed or restored.</Alert>}
          {!!summary?.rejected && <Alert severity="warning" sx={{ mt: 2 }}>{summary.rejected.toLocaleString()} {summary.rejected === 1 ? 'row needs' : 'rows need'} correction and will not be imported. Download the rows to correct and import them separately after fixing them.</Alert>}
          {!!summary?.defaulted && <DialogContentText sx={{ mt: 2 }}>{summary.defaulted.toLocaleString()} {summary.defaulted === 1 ? 'row uses' : 'rows use'} defaults for blank settings. Review the effective values before continuing.</DialogContentText>}
        </DialogContent>
        <DialogActions>
          <Button disabled={!!busy} onClick={() => setConfirmOpen(false)}>Keep reviewing</Button>
          <Button variant="contained" disabled={!!busy || loading || !!error || !summary?.ready} onClick={confirm}>Create accounts</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={blocker.state === 'blocked'} onClose={() => blocker.reset?.()} aria-labelledby="leave-import-title">
        <DialogTitle id="leave-import-title">Leave this import?</DialogTitle>
        <DialogContent>
          <DialogContentText>{busy ? 'An operation is in progress. Wait for the saved result before leaving this page.' : <>No accounts will be created. {importId ? 'The saved preview will remain available in import history if you want to return to it.' : 'You will need to choose your file again when you return.'}</>}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => blocker.reset?.()}>Keep reviewing</Button>
          <Button variant="contained" disabled={!!busy} onClick={() => blocker.proceed?.()}>Leave import</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
